import { isFolderType, toTreeItem } from '../../constants'
import { StructureTreeItem } from '../../types'

export interface ProcessChildrenResult {
	children: StructureTreeItem[]
	grandchildrenByParent: Record<string, StructureTreeItem[]>
	leafNodeEntries: Record<string, StructureTreeItem[]>
}

export const sortTreeItems = (
	items: StructureTreeItem[],
	alphabetSort: boolean,
): StructureTreeItem[] => {
	return [...items].sort((a, b) => {
		const aIsFolder = isFolderType(a.type)
		const bIsFolder = isFolderType(b.type)
		if (aIsFolder && !bIsFolder) return -1
		if (!aIsFolder && bIsFolder) return 1

		if (!alphabetSort) {
			if (a.orderPriority != null && b.orderPriority != null) {
				return a.orderPriority - b.orderPriority
			}
			if (a.orderPriority != null) return -1
			if (b.orderPriority != null) return 1
		}

		return (a.label || '').localeCompare(b.label || '')
	})
}

export const processChildrenResponse = (
	result: { parent?: { id?: number } }[],
	parentId: number,
): ProcessChildrenResult => {
	const directChildren = result.filter((item) => item.parent?.id === parentId)
	const rawChildren = directChildren.map(toTreeItem)
	const childIds = new Set(rawChildren.map((c) => c.id))

	const grandchildrenByParent: Record<string, StructureTreeItem[]> = {}
	const parentIdsWithChildren = new Set<string>()
	const idsWithChildren = new Set<string>()

	result.forEach((item) => {
		if (item.parent?.id == null) return

		const itemParentId = String(item.parent.id)
		idsWithChildren.add(itemParentId)

		if (childIds.has(itemParentId)) {
			parentIdsWithChildren.add(itemParentId)
			if (!grandchildrenByParent[itemParentId]) {
				grandchildrenByParent[itemParentId] = []
			}
			grandchildrenByParent[itemParentId].push(toTreeItem(item))
		}
	})

	Object.values(grandchildrenByParent).forEach((grandchildren) => {
		grandchildren.forEach((child) => {
			child.hasChildren = idsWithChildren.has(child.id)
		})
	})

	const children = rawChildren.map((child) => ({
		...child,
		hasChildren: parentIdsWithChildren.has(child.id),
	}))

	const leafNodeEntries: Record<string, StructureTreeItem[]> = {}
	const markLeaves = (items: StructureTreeItem[]) => {
		items.forEach((item) => {
			if (!item.hasChildren) {
				leafNodeEntries[item.id] = []
			}
		})
	}
	markLeaves(children)
	Object.values(grandchildrenByParent).forEach(markLeaves)

	return { children, grandchildrenByParent, leafNodeEntries }
}

